import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../../api/hooks';
import { useCrumbs } from '../../layout/crumbs';
import { QueryView } from '../../ui/feedback';
import { usePageTitle } from '../../ui/misc';
import { AdminDecisions } from './attempt/AdminDecisions';
import { AdminNoteDialog } from './attempt/AdminNoteDialog';
import { BindingDetails } from './attempt/BindingDetails';
import { DeployRunCard } from './attempt/DeployRun';
import { AttemptHeader } from './attempt/Header';
import { ManualCheckDialog } from './attempt/ManualCheckDialog';
import { ReconcileDialog } from './attempt/ReconcileDialog';
import { ReportDialog } from './attempt/ReportDialog';
import { ReportedResult } from './attempt/ReportedResult';
import { AttemptStatus } from './attempt/Status';
import { TicketEffects } from './attempt/TicketEffects';
import type { AttemptDetail } from './types';

/** WF-12–16: shared deployment/rollback attempt loading, routing and composition. */
export function AttemptPage({ rollback }: { rollback?: boolean }) {
  const { key, releaseId, rollbackId, attemptId } = useParams();
  const base = rollback
    ? `/projects/${key}/releases/${releaseId}/rollbacks/${rollbackId}/attempts/${attemptId}`
    : `/projects/${key}/releases/${releaseId}/attempts/${attemptId}`;
  const q = useQuery({ queryKey: ['attempt', base], queryFn: () => api.get<AttemptDetail>(base) });
  usePageTitle(
    q.data
      ? rollback
        ? `Rollback ${rollbackId} · Attempt ${q.data.attempt}`
        : `Release ${releaseId} · Deployment attempt ${q.data.attempt}`
      : null,
  );
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: `Release ${releaseId}`, to: `/projects/${key}/releases/${releaseId}` },
    ...(rollback
      ? [
          {
            label: `Rollback ${rollbackId}`,
            to: `/projects/${key}/releases/${releaseId}/rollbacks/${rollbackId}`,
          },
        ]
      : []),
    { label: attemptId ?? '' },
  ]);
  const [dialog, setDialog] = useState<'report' | 'reconcile' | 'verify' | 'correct' | null>(null);
  const [checkDialog, setCheckDialog] = useState<string | null>(null);
  const inv = [['attempt', base], ['release', key, releaseId], ['releases', key], ['ticket']];
  return (
    <div className="page">
      <QueryView query={q}>
        {(a) => (
          <>
            <AttemptHeader a={a} rollback={rollback} setDialog={setDialog} />
            <AttemptStatus a={a} />
            <div className="split">
              <div className="stack">
                <DeployRunCard a={a} setCheckDialog={setCheckDialog} />
                <ReportedResult a={a} />
                <TicketEffects a={a} />
                <AdminDecisions a={a} />
              </div>
              <div className="stack">
                <BindingDetails a={a} />
              </div>
            </div>
            {dialog === 'report' && (
              <ReportDialog a={a} base={base} onClose={() => setDialog(null)} inv={inv} />
            )}
            {dialog === 'reconcile' && (
              <ReconcileDialog a={a} base={base} onClose={() => setDialog(null)} inv={inv} />
            )}
            {(dialog === 'verify' || dialog === 'correct') && (
              <AdminNoteDialog kind={dialog} base={base} onClose={() => setDialog(null)} inv={inv} />
            )}
            {checkDialog && (
              <ManualCheckDialog
                a={a}
                name={checkDialog}
                base={base}
                onClose={() => setCheckDialog(null)}
                inv={inv}
              />
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}
